/**
 * `v-perm` 指令 —— 09 章 §9.2.4 硬规则 3。
 *
 * **无权限直接移除节点，而不是 disabled。**
 * 一个 disabled 的「标记完成」按钮会让 trainer 反复追问为什么点不了；
 * 移除之后他根本不知道有这个功能，也就不会问。
 *
 * 用法：
 *   <AppButton v-perm="'job:finalize'">标记完成</AppButton>
 *   <AppButton v-perm="['dataset:create', 'dataset:update']">编辑</AppButton>   任一满足
 */

import type { Directive } from 'vue'

import { useAuthStore } from '@/stores/auth'

function allowed(value: unknown): boolean {
  const auth = useAuthStore()
  const need = Array.isArray(value) ? value : [value]
  if (need.length === 0) return true
  return need.some((p) => typeof p === 'string' && auth.perms.has(p))
}

export const permDirective: Directive<HTMLElement, string | string[]> = {
  mounted(el, binding) {
    if (!allowed(binding.value)) {
      // 用「移出文档」而不是 display:none —— 后者仍在文档里，
      // 改一下 CSS 就能点出来，会让人误以为前端权限是一道防线。
      //
      // 但**不能**用 `el.remove()`：那样 `el.parentNode` 变成 `null`，而 Vue
      //   在 patch 同一元素上的 `v-if` 时要拿 `hostParentNode(oldVNode.el)` 当容器
      //   （`patchBlockChildren`：新旧类型不同 —— 元素 → 注释占位符 —— 就走这一支）。
      //   容器是 null，`insertBefore` 立刻抛 `Cannot read properties of null`，
      //   而这个异常发生在渲染副作用里：**整个页面从此不再更新**，看上去像「卡死」。
      //   触发条件是「没这个权限的人 + 同元素上的 v-if 翻假」，例如 viewer 在
      //   评测详情页点刷新（`v-if="report"` 短暂变 null）。
      //
      //   放进一个 DocumentFragment 就同时满足两件事：节点不在文档里
      //   （`querySelector` 找不到、CSS 碰不到、不可点不可聚焦），
      //   但 `parentNode` 不是 null，Vue 把占位符插进这个 fragment 也无害。
      document.createDocumentFragment().appendChild(el)
    }
  },
}
